/**
 * Doigtés pianistiques classiques pour les 12 gammes majeures, ascendantes,
 * sur une octave (8 cases : 7 degrés + reprise de la tonique à l'octave).
 *
 * Conventions (école française / Hanon) :
 * - 1 = pouce, 2 = index, 3 = majeur, 4 = annulaire, 5 = auriculaire.
 * - Le pouce évite autant que possible les touches noires.
 * - Les passages du pouce sont les indices où le doigt 1 apparaît
 *   ailleurs qu'à la position d'attaque (RH start, LH end). Ces
 *   passages sont mis en évidence visuellement dans HarmonyScaleDetail.
 *
 * Référence : Hanon "Le pianiste virtuose", section gammes majeures.
 *
 * Le mode mineur n'est pas couvert ici, les doigtés varient selon la
 * forme (naturelle, harmonique, mélodique) et il n'y a pas de standard
 * universellement partagé. À ajouter dans une seconde étape avec une
 * référence fiable.
 */

export interface ScaleFingering {
  /** RH ascending, 8 fingers (7 degrés + octave). */
  rh: readonly number[];
  /** LH ascending, 8 fingers. */
  lh: readonly number[];
  /** Indices RH où le pouce passe sous (toujours dérivés mais hard-codés pour clarté). */
  rhCrossings: readonly number[];
  /** Indices LH où le pouce passe (cf. rhCrossings). */
  lhCrossings: readonly number[];
}

export const MAJOR_SCALE_FINGERINGS: Record<string, ScaleFingering> = {
  // ── Tonalités sans dièse ni bémol, ou avec touches blanches uniquement
  //    pour le pouce, schéma "Do majeur" canonique.
  C:  { rh: [1, 2, 3, 1, 2, 3, 4, 5], lh: [5, 4, 3, 2, 1, 3, 2, 1], rhCrossings: [3], lhCrossings: [4] },
  G:  { rh: [1, 2, 3, 1, 2, 3, 4, 5], lh: [5, 4, 3, 2, 1, 3, 2, 1], rhCrossings: [3], lhCrossings: [4] },
  D:  { rh: [1, 2, 3, 1, 2, 3, 4, 5], lh: [5, 4, 3, 2, 1, 3, 2, 1], rhCrossings: [3], lhCrossings: [4] },
  A:  { rh: [1, 2, 3, 1, 2, 3, 4, 5], lh: [5, 4, 3, 2, 1, 3, 2, 1], rhCrossings: [3], lhCrossings: [4] },
  E:  { rh: [1, 2, 3, 1, 2, 3, 4, 5], lh: [5, 4, 3, 2, 1, 3, 2, 1], rhCrossings: [3], lhCrossings: [4] },

  // ── Si majeur : LH démarre sur 4 (le 5 sur Si est inconfortable).
  B:  { rh: [1, 2, 3, 1, 2, 3, 4, 5], lh: [4, 3, 2, 1, 4, 3, 2, 1], rhCrossings: [3], lhCrossings: [3] },

  // ── Fa majeur : seul Si♭ noir, le pouce passe plus tard (index 4).
  F:  { rh: [1, 2, 3, 4, 1, 2, 3, 4], lh: [5, 4, 3, 2, 1, 3, 2, 1], rhCrossings: [4], lhCrossings: [4] },

  // ── Tonalités à départ noir : RH commence sur 2/3/4, jamais sur 1.
  //    "F#" et "Gb" pointent vers la même entrée.
  "F#": { rh: [2, 3, 4, 1, 2, 3, 1, 2], lh: [4, 3, 2, 1, 3, 2, 1, 4], rhCrossings: [3, 6], lhCrossings: [3, 6] },
  Gb:   { rh: [2, 3, 4, 1, 2, 3, 1, 2], lh: [4, 3, 2, 1, 3, 2, 1, 4], rhCrossings: [3, 6], lhCrossings: [3, 6] },

  Bb: { rh: [4, 1, 2, 3, 1, 2, 3, 4], lh: [3, 2, 1, 4, 3, 2, 1, 3], rhCrossings: [1, 4], lhCrossings: [2, 6] },
  Eb: { rh: [3, 1, 2, 3, 1, 2, 3, 4], lh: [3, 2, 1, 4, 3, 2, 1, 3], rhCrossings: [1, 4], lhCrossings: [2, 6] },
  Ab: { rh: [3, 4, 1, 2, 3, 1, 2, 3], lh: [3, 2, 1, 3, 2, 1, 4, 3], rhCrossings: [2, 5], lhCrossings: [2, 5] },

  // "Db" et "C#" pointent vers la même entrée enharmonique.
  Db:   { rh: [2, 3, 1, 2, 3, 4, 1, 2], lh: [3, 2, 1, 4, 3, 2, 1, 3], rhCrossings: [2, 6], lhCrossings: [2, 6] },
  "C#": { rh: [2, 3, 1, 2, 3, 4, 1, 2], lh: [3, 2, 1, 4, 3, 2, 1, 3], rhCrossings: [2, 6], lhCrossings: [2, 6] },
};

/**
 * Renvoie le doigté pour une tonalité majeure donnée, ou `null` si la
 * tonalité n'est pas couverte (typiquement : mode mineur, tonique
 * exotique). Le call site doit alors masquer la section "Doigtés".
 */
export function getMajorFingering(tonic: string): ScaleFingering | null {
  return MAJOR_SCALE_FINGERINGS[tonic] ?? null;
}
